import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { NotificationService } from '../../../shared/components/ui/notification/notification.service';
import { CategoryService } from '../../../services/category.service';
import { CategoryResponse } from '../../../dto/category.response';
import { CategoryRequest } from '../../../dto/category.request';

@Component({
    selector: 'app-categories',
    standalone: true,
    imports: [CommonModule, FormsModule, PageBreadcrumbComponent, BadgeComponent],
    templateUrl: './categories.component.html',
})
export class CategoriesComponent implements OnInit {

    items: CategoryResponse[] = [];
    loading = false;
    searchTerm = '';

    // Pagination
    currentPage = 1;
    itemsPerPage = 10;
    readonly pageSizeOptions = [10, 20, 50];

    // Form modal
    showForm = false;
    isEditing = false;
    submitting = false;
    formSubmitted = false;
    editingId: number | null = null;
    formName = '';
    formDescription = '';

    // Inline toggle
    togglingId: number | null = null;

    get filtered(): CategoryResponse[] {
        const t = this.searchTerm.trim().toLowerCase();
        return t ? this.items.filter(i => i.name.toLowerCase().includes(t) || (i.description ?? '').toLowerCase().includes(t)) : this.items;
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.filtered.length / this.itemsPerPage));
    }

    get currentItems(): CategoryResponse[] {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        return this.filtered.slice(start, start + this.itemsPerPage);
    }

    get formError(): string {
        if (!this.formName.trim()) return 'El nombre es obligatorio';
        return '';
    }

    constructor(
        private service: CategoryService,
        private notify: NotificationService,
    ) {}

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        this.loading = true;
        this.service.getAll().subscribe({
            next: res => { this.items = res.data ?? []; this.loading = false; },
            error: () => { this.notify.error('Error al cargar categorías'); this.loading = false; }
        });
    }

    setSearchTerm(val: string): void { this.searchTerm = val; this.currentPage = 1; }
    goToPage(page: number): void { if (page >= 1 && page <= this.totalPages) this.currentPage = page; }
    onPageSizeChange(size: number): void { this.itemsPerPage = size; this.currentPage = 1; }

    openCreate(): void {
        this.isEditing = false;
        this.editingId = null;
        this.formSubmitted = false;
        this.formName = '';
        this.formDescription = '';
        this.showForm = true;
    }

    openEdit(item: CategoryResponse): void {
        this.isEditing = true;
        this.editingId = item.id;
        this.formSubmitted = false;
        this.formName = item.name;
        this.formDescription = item.description ?? '';
        this.showForm = true;
    }

    closeForm(): void { this.showForm = false; }

    save(): void {
        this.formSubmitted = true;
        if (this.formError) return;
        this.submitting = true;
        const req: CategoryRequest = {
            name: this.formName.trim(),
            description: this.formDescription.trim() || undefined,
        };
        const op$ = this.isEditing
            ? this.service.update(this.editingId!, req)
            : this.service.create(req);

        op$.subscribe({
            next: res => {
                this.notify.success(res.message ?? 'Guardado correctamente');
                this.submitting = false;
                this.showForm = false;
                this.load();
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'Error al guardar');
                this.submitting = false;
            }
        });
    }

    toggleStatus(item: CategoryResponse): void {
        const newStatus = item.status === 1 ? 0 : 1;
        this.togglingId = item.id;
        this.service.updateStatus(item.id, newStatus).subscribe({
            next: () => {
                item.status = newStatus;
                this.notify.success(`Categoría ${newStatus === 1 ? 'activada' : 'inactivada'}`);
                this.togglingId = null;
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'Error al cambiar estado');
                this.togglingId = null;
            }
        });
    }
}
